import './tagSection.scss';

import React, { useId, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { InputState } from '../input';

type Props = {
  /** Holds the tags as one comma-separated string, the shape the job synchronizer already parses. */
  model: InputState;
  label?: string;
  className?: string;
};

export function splitTags(value: unknown): string[] {
  return String(value ?? '').split(',').map(tag => tag.trim()).filter(Boolean);
}

export const TagsInput = observer(({ model, label, className }: Props) => {
  const id = useId();
  const draftRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const tags = splitTags(model.value);
  const setTags = (next: string[]) => model.handleChange(null, next.join(', '));

  const commit = (text: string) => {
    const added = splitTags(text).filter(tag => !tags.some(t => t.toLowerCase() === tag.toLowerCase()));
    if (added.length) setTags([...tags, ...added]);
    setDraft('');
  };

  return (
    <div className={classNames('tags-input', className, { disabled: model.disabled })}>
      <label className="input-label" htmlFor={id}>{label ?? model.label}</label>
      <div className="tags-input-field" onClick={() => draftRef.current?.focus()}>
        {tags.map(tag => (
          <span key={tag} className="tag-chip" title={tag}>
            <span className="value">#{tag}</span>
            <button
              type="button"
              className="tag-chip-remove"
              disabled={model.disabled}
              aria-label={`Remove tag ${tag}`}
              onClick={() => setTags(tags.filter(t => t !== tag))}>
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={draftRef}
          className="tags-input-draft"
          value={draft}
          disabled={model.disabled}
          placeholder={tags.length ? '' : 'Type a tag and press Enter'}
          onChange={evt => {
            const { value } = evt.target;
            const lastComma = value.lastIndexOf(',');
            if (lastComma < 0) return setDraft(value);
            commit(value.slice(0, lastComma));
            setDraft(value.slice(lastComma + 1).trimStart());
          }}
          onKeyDown={evt => {
            if (evt.key === 'Enter') {
              evt.preventDefault();
              commit(draft);
            } else if (evt.key === 'Backspace' && !draft && tags.length) {
              setTags(tags.slice(0, -1));
            }
          }}
          onBlur={() => commit(draft)}
        />
      </div>
    </div>
  );
});
